import React, { useEffect, useState } from "react";
import useSWR from "swr";
import axios from "axios";
import { Alert, Form, message } from "antd";
import { useLocation } from "react-router-dom";

const backendUrl = process.env.REACT_APP_BACKEND_URL;


interface ApiDataType {
    id: number;
    verifikasi: string;
    receipt_no: string;
    status_invoice: string;
    date_created: string | null;
    date_verify: string | null;
    transaction_to: string | null;
    no_invoice: string;
    invoice_date: string | null;
    customer_code: string;
    customer_name: string;
    address: string | null;
    salesman: string | null;
    top: string | null;
    remark: string | null;
    invoice_amount: string;
    saldo_payment: string;
    saldo_akhir: string;
    warehouse: string | null;
}


export interface MappedDataType {
    key: React.Key;
    verifikasi: string;
    receiptNo: string;
    statusInvoice: string;
    dateCreated: string | null;
    dateVerify: string | null;
    transactionTo: string | null;
    invoiceNo: string;
    invoiceDate: string | null;
    customerCode: string;
    customerName: string;
    address: string | null;
    salesman: string | null;
    top: string | null;
    remark: string | null;
    amountInvoice: number;
    amountPayment: number;
    balanceCredit: number;
    warehouse: string | null;
}

const fetcher = (url: string) => axios.get(url).then(res => res.data);

const useDataCashInvoiceLogic = () => {
    const { data: apiData, error, isLoading } = useSWR<ApiDataType[]>(`${backendUrl}/api/data-cash-invoices`, fetcher);

    const mappedData = React.useMemo(() => {
        if (!apiData) return [];
        return apiData.map((item): MappedDataType => ({
            key: item.id,
            receiptNo: item.receipt_no,
            statusInvoice: item.status_invoice,
            dateCreated: item.date_created,
            dateVerify: item.date_verify,
            transactionTo: item.transaction_to,
            invoiceNo: item.no_invoice,
            invoiceDate: item.invoice_date,
            customerCode: item.customer_code,
            customerName: item.customer_name,
            address: item.address,
            salesman: item.salesman,
            top: item.top,
            remark: item.remark,
            verifikasi: item.verifikasi,
            warehouse: item.warehouse,
            amountInvoice: parseFloat(item.invoice_amount) || 0,
            amountPayment: parseFloat(item.saldo_payment) || 0,
            balanceCredit: parseFloat(item.saldo_akhir) || 0,
        }));
    }, [apiData]);

    return {
        data: mappedData,
        isLoading,
        error,
    };
};

interface InjectedProps {
    data: MappedDataType[];
    isLoading: boolean;
}

export const withDataCashInvoiceLogic = <P extends object>(WrappedComponent: React.ComponentType<P & InjectedProps>) => {
    return (props: P) => {
        const { data, isLoading, error } = useDataCashInvoiceLogic();

        if (error) {
            return <Alert message="Error" description="Gagal memuat data dari server." type="error" showIcon />;
        }

        return <WrappedComponent {...props} data={data} isLoading={isLoading} />;
    };
};

const getStatusInvoiceLabel = (status: string): string => {
    switch (status) {
        case '1':
            return 'Active';
        case '2':
            return 'Lunas';
        case '3':
            return 'Jatuh Tempo';
        default:
            return status;
    }
};

const formatDateToDDMMYYYY = (dateString: string | null): string | null => {
    if (!dateString) return null;
    try {
        const date = new Date(dateString);

        if (isNaN(date.getTime())) {
            return null;
        }
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    } catch (error) {

        return null;
    }
};

interface InjectedDetailProps {
    form: any;
    currentRecord: MappedDataType | null;
    handleVerify: () => Promise<void>;
    handleUpdate: () => Promise<void>;
}

interface InjectedDetailProps {
    form: any;
    currentRecord: MappedDataType | null;
    handleVerify: () => Promise<void>;
}

export const withDataCashInvoiceDetailLogic = <P extends object>(WrappedComponent: React.ComponentType<P & InjectedDetailProps>) => {
    return (props: P) => {
        const [form] = Form.useForm();
        const location = useLocation();

        const [currentRecord, setCurrentRecord] = useState<MappedDataType | null>(null);

        const handleVerify = async () => {
            if (!currentRecord) {
                message.error("Tidak ada data yang dipilih untuk diverifikasi.");
                return;
            }
            try {
                const response = await axios.put(`${backendUrl}/api/data-cash-invoices/${currentRecord.key}/verify`);

                if (response.status === 200 && response.data.data) {
                    const updatedDataFromApi = response.data.data;

                    const updatedRecord = {
                        ...currentRecord,
                        verifikasi: '1',
                        dateVerify: updatedDataFromApi.date_verify 
                    };
                    setCurrentRecord(updatedRecord);
                    form.setFieldsValue({
                        verifikasi: '1',
                        date_verify: updatedDataFromApi.date_verify
                    });
                    message.success("Invoice berhasil diverifikasi!");
                } else {
                    throw new Error("Invalid response from server");
                }
            } catch (error) {
                console.error("Verifikasi gagal:", error);
                message.error("Gagal memverifikasi invoice. Silakan coba lagi.");
            }
        };

        const handleUpdate = async () => {
            if (!currentRecord) {
                message.error("Tidak ada data yang dipilih untuk diperbarui.");
                return;
            }
            try {
                const values = await form.validateFields(['remark']);
                const response = await axios.put(`${backendUrl}/api/data-cash-invoices/${currentRecord.receiptNo}/update`, {
                    remark: values.remark,
                });

                if (response.status === 200) {
                    message.success("Remark berhasil diperbarui!");
                }
            } catch (error) {
                console.error("Pembaruan gagal:", error);
                message.error("Gagal memperbarui remark. Silakan coba lagi.");
            }
        };

        useEffect(() => {
            const record = location.state?.record;
            if (record) {
                setCurrentRecord(record);
                form.setFieldsValue({
                    receipt_no: record.receiptNo,
                    status_invoice: getStatusInvoiceLabel(record.statusInvoice),
                    date_created: record.dateCreated,
                    date_verify: record.dateVerify,
                    transaction_to: record.transactionTo,
                    no_invoice: record.invoiceNo,
                    customer_code: record.customerCode,
                    customer_name: record.customerName,
                    address: record.address,
                    salesman: record.salesman,
                    top: record.top,
                    remark: record.remark,
                    verifikasi: record.verifikasi,
                    invoice_amount: new Intl.NumberFormat('id-ID').format(record.amountInvoice),
                    saldo_payment: new Intl.NumberFormat('id-ID').format(record.amountPayment),
                    saldo_akhir: new Intl.NumberFormat('id-ID').format(record.balanceCredit),
                });
            }
        }, [location.state, form]);

        return <WrappedComponent {...props} form={form} currentRecord={currentRecord} handleVerify={handleVerify} handleUpdate={handleUpdate} />;
    };
};